import React, { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom';
import './ShowsPage.scss';
import { easeOut, motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import Lenis from 'lenis';

const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${day}.${month}.${year}`;
};


const ShowsPage = ({ shows }) => {
    // Commented out scroll restoration hook for reference
    //useScrollRestoration('shows');

    const columnContentRef = useRef(null);
    const columnContentInnerRef = useRef(null);

    useEffect(() => {
        if (!columnContentRef.current || !columnContentInnerRef.current) return;

        const lenis = new Lenis({
            wrapper: columnContentRef.current,
            content: columnContentInnerRef.current,
            wheelEventsTarget: columnContentRef.current,
            touchEventsTarget: columnContentRef.current,
            smoothWheel: true,
            wheelMultiplier: 0.8,
            touchMultiplier: 0.9,
        });

        let rafId;
        function raf(time) {
            lenis.raf(time);
            rafId = requestAnimationFrame(raf);
        }

        rafId = requestAnimationFrame(raf);

        return () => {
            lenis.destroy();
            cancelAnimationFrame(rafId);
        };
    }, [shows]);

    // Calculate the threshold: hide shows more than 2 days before now
    const threshold = new Date();
    threshold.setDate(threshold.getDate() - 2);
    threshold.setHours(0, 0, 0, 0);

    // Filter out shows that are more than 2 days in the past and sort by date
    const upcomingShows = shows
        .filter(show => new Date(show.date) >= threshold)
        .sort((a, b) => new Date(a.date) - new Date(b.date));

    return (
        // Page container with fade animation
        <>
            <Helmet>
                <title>shows - nthng</title>
                <meta name="description" content="Upcoming shows by nthng" />
                <link rel="canonical" href="https://nthng.nl/shows" />
                <meta property="og:title" content="Shows - nthng" />
                <meta property="og:description" content="Upcoming shows by nthng" />
                <meta property="og:url" content="https://nthng.nl/shows" />
                <meta property="og:type" content="website" />
                <meta property="og:image" content="https://nthng.nl/content/nthng_unfinished.webp" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Shows - nthng" />
                <meta name="twitter:description" content="Upcoming shows by nthng" />
                <meta name="twitter:image" content="https://nthng.nl/content/nthng_unfinished.webp" />
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "ItemList",
                        "name": "nthng Upcoming Shows",
                        "description": "Upcoming live shows and DJ sets by nthng",
                        "url": "https://nthng.nl/shows",
                        "numberOfItems": upcomingShows.length,
                        "itemListElement": upcomingShows.slice(0, 10).map((show, index) => ({
                            "@type": "ListItem",
                            "position": index + 1,
                            "item": {
                                "@type": "MusicEvent",
                                "name": show.title,
                                "startDate": show.date,
                                "location": {
                                    "@type": "Place",
                                    "name": show.location
                                },
                                "performer": {
                                    "@type": "MusicGroup",
                                    "name": "nthng"
                                }
                            }
                        }))
                    })}
                </script>
            </Helmet>
            <motion.div
                ref={columnContentRef}
                className="shows-container"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25, ease: easeOut }}
            >
                <section ref={columnContentInnerRef} className=''>
                    {/* Map through upcoming shows and display each one */}
                    {upcomingShows.map(show => (
                        <div key={show.id} className="show-row">
                            <p className='show-date'>{formatDate(show.date)}</p>
                            <p className='show-title'><Link to={show.link} target='_blank'>{show.title}</Link></p>
                            <p className='show-location'>{show.location}</p>
                            <p className='show-category'>{show.category}</p>
                        </div>
                    ))}
                    {/* Newsletter signup link */}
                    <div className='get-notified'><Link to="/newsletter">get notified for shows in your area d-.-b</Link></div>
                </section>
            </motion.div>
        </>
    )
}

export default ShowsPage